import React, { Component } from "react";
import axios from "axios";

const config = require("../config.json");

export default class RecommendationAdmin extends Component {
  state = {
    campaigns: [],
    activities: [],
    recommendations: [],
    nextToken: null,
    loading: false,
    loadingMore: false,
    selectedCampaignId: "",
    selectedActivityId: "",
    selectedScopeType: "GLOBAL",
    newRecommendation: {
      title: "",
      text: "",
      link: "",
    },
  };

  componentDidMount = async () => {
    await Promise.all([this.fetchCampaigns(), this.fetchActivities()]);
  };

  fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${config.api.invokeUrl}/campaign`);
      const campaigns = (res.data ?? []).sort((a, b) =>
        `${a.campaignName}`.localeCompare(`${b.campaignName}`),
      );

      this.setState(
        (prevState) => ({
          campaigns,
          selectedCampaignId:
            prevState.selectedCampaignId || campaigns[0]?.id || "",
        }),
        () => {
          this.fetchRecommendations();
        },
      );
    } catch (err) {
      console.log(err);
    }
  };

  fetchActivities = async () => {
    try {
      const res = await axios.get(`${config.api.invokeUrl}/activities`);
      const activities = (res.data ?? [])
        .filter((activity) => activity.recommendableActivity)
        .sort((a, b) => `${a.activityName}`.localeCompare(`${b.activityName}`));

      this.setState(
        (prevState) => ({
          activities,
          selectedActivityId:
            prevState.selectedActivityId || activities[0]?.id || "",
        }),
        () => {
          this.fetchRecommendations();
        },
      );
    } catch (err) {
      console.log(err);
    }
  };

  fetchRecommendations = async (nextToken = null) => {
    const { selectedCampaignId, selectedActivityId, selectedScopeType } =
      this.state;

    if (!selectedActivityId) {
      this.setState({
        recommendations: [],
        nextToken: null,
      });
      return;
    }

    if (selectedScopeType === "CAMPAIGN" && !selectedCampaignId) {
      this.setState({
        recommendations: [],
        nextToken: null,
      });
      return;
    }

    this.setState({ loading: !nextToken, loadingMore: !!nextToken });

    try {
      const params = {
        activityId: selectedActivityId,
        scopeType: selectedScopeType,
        limit: 100,
      };

      if (selectedCampaignId) {
        params.campaignId = selectedCampaignId;
      }

      if (nextToken && selectedScopeType !== "ALL") {
        params.nextToken = nextToken;
      }

      const res = await axios.get(`${config.api.invokeUrl}/recommendations`, {
        params,
      });

      this.setState((prevState) => ({
        recommendations:
          nextToken && selectedScopeType !== "ALL"
            ? [...prevState.recommendations, ...(res.data?.recommendations ?? [])]
            : res.data?.recommendations ?? [],
        nextToken:
          selectedScopeType === "ALL" ? null : res.data?.nextToken ?? null,
      }));
    } catch (err) {
      console.log(err);
    }

    this.setState({ loading: false, loadingMore: false });
  };

  handleFilterChange = (event) => {
    const { name, value } = event.target;

    this.setState(
      {
        [name]: value,
      },
      () => {
        this.fetchRecommendations();
      },
    );
  };

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    this.setState({
      newRecommendation: {
        ...this.state.newRecommendation,
        [name]: value,
      },
    });
  };

  handleAddRecommendation = async (event) => {
    event.preventDefault();

    const { selectedActivityId, newRecommendation } = this.state;

    if (!selectedActivityId) {
      window.alert("Vælg en aktivitet først.");
      return;
    }

    try {
      const params = {
        activityId: selectedActivityId,
        title: newRecommendation.title,
        text: newRecommendation.text,
        link: newRecommendation.link,
        createdByUserId: this.props.auth?.user?.attributes?.sub
          ? this.props.auth.user.attributes.sub
          : this.props.auth?.user?.username ?? "",
        createdByName: this.props.auth?.user?.username ?? "Admin",
        createdByType: "admin",
        scopeType: "GLOBAL",
      };

      await axios.post(`${config.api.invokeUrl}/recommendations`, params);

      this.setState(
        {
          selectedScopeType: "GLOBAL",
          newRecommendation: {
            title: "",
            text: "",
            link: "",
          },
        },
        () => this.fetchRecommendations(),
      );
    } catch (err) {
      window.alert(
        err?.response?.data?.message ??
          "Kunne ikke oprette anbefalingen. Tjek evt. link-formatet.",
      );
      console.log(err);
    }
  };

  handleDeleteRecommendation = async (recommendationId, event) => {
    event.preventDefault();

    if (
      !window.confirm("Dette vil slette anbefalingen for evigt. Er du sikker?")
    ) {
      return;
    }

    try {
      await axios.delete(
        `${config.api.invokeUrl}/recommendations/${recommendationId}`,
      );

      this.setState((prevState) => ({
        recommendations: prevState.recommendations.filter(
          (recommendation) => recommendation.id !== recommendationId,
        ),
      }));
    } catch (err) {
      console.log(err);
    }
  };

  getCampaignName = (campaignId) =>
    this.state.campaigns.find((campaign) => campaign.id == campaignId)
      ?.campaignName ?? campaignId;

  renderSelectedNames = () => {
    const selectedCampaign = this.state.campaigns.find(
      (campaign) => campaign.id == this.state.selectedCampaignId,
    );
    const selectedActivity = this.state.activities.find(
      (activity) => activity.id == this.state.selectedActivityId,
    );

    return (
      <p className="subtitle is-6">
        {selectedActivity ? `Aktivitet: ${selectedActivity.activityName}` : ""}
        {selectedActivity && this.state.selectedScopeType !== "GLOBAL"
          ? " | "
          : ""}
        {this.state.selectedScopeType === "GLOBAL" && "Viser globale anbefalinger"}
        {this.state.selectedScopeType === "CAMPAIGN" && selectedCampaign
          ? `Kampagne: ${selectedCampaign.campaignName}`
          : ""}
        {this.state.selectedScopeType === "ALL" &&
          `Viser globale${
            selectedCampaign ? ` + kampagne: ${selectedCampaign.campaignName}` : ""
          }`}
      </p>
    );
  };

  render() {
    return (
      <>
        <p className="subtitle is-5">Opret og se anbefalinger</p>
        <p>Admin-oprettede anbefalinger bliver oprettet globalt på tværs af kampagner.</p>
        {this.renderSelectedNames()}
        <div className="columns is-multiline">
          <div className="column is-half">
            <form onSubmit={this.handleAddRecommendation}>
              <div className="field">
                <div className="control">
                  Aktivitet:
                  <select
                    name="selectedActivityId"
                    value={this.state.selectedActivityId}
                    onChange={this.handleFilterChange}
                  >
                    <option value=""></option>
                    {this.state.activities.map((activity) => (
                      <option key={activity.id} value={activity.id}>
                        {activity.activityName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="control">
                  <input
                    name="title"
                    className="input is-medium"
                    type="text"
                    placeholder="Indtast titel"
                    value={this.state.newRecommendation.title}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
                <div className="control">
                  <textarea
                    name="text"
                    className="textarea"
                    placeholder="Indtast anbefalingen"
                    value={this.state.newRecommendation.text}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
                <div className="control">
                  <input
                    name="link"
                    className="input is-medium"
                    type="text"
                    placeholder="Indsæt link (valgfrit)"
                    value={this.state.newRecommendation.link}
                    onChange={this.handleInputChange}
                  />
                </div>
                <div className="control">
                  <button type="submit" className="button is-primary is-medium">
                    Opret global anbefaling
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div className="column is-half">
            <div className="field">
              <div className="control">
                Vis:
                <select
                  name="selectedScopeType"
                  value={this.state.selectedScopeType}
                  onChange={this.handleFilterChange}
                >
                  <option value="GLOBAL">Globale</option>
                  <option value="CAMPAIGN">Kampagnespecifikke</option>
                  <option value="ALL">Alle</option>
                </select>
              </div>
            </div>
            {this.state.selectedScopeType !== "GLOBAL" && (
              <div className="field">
                <div className="control">
                  Kampagne:
                  <select
                    name="selectedCampaignId"
                    value={this.state.selectedCampaignId}
                    onChange={this.handleFilterChange}
                  >
                    <option value=""></option>
                    {this.state.campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.campaignName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div className="field">
              <div className="control">
                <button
                  className="button is-light"
                  onClick={() => this.fetchRecommendations()}
                  type="button"
                >
                  Opdater liste
                </button>
              </div>
            </div>
            {this.state.loading && <p>Henter anbefalinger...</p>}
            {!this.state.loading && !this.state.recommendations.length && (
              <p>Ingen anbefalinger fundet for det valgte filter.</p>
            )}
            <div className="is-flex is-flex-direction-column">
              {this.state.recommendations.map((recommendation) => (
                <div className="box notification" key={recommendation.id}>
                  <button
                    onClick={(event) =>
                      this.handleDeleteRecommendation(recommendation.id, event)
                    }
                    className="delete"
                  ></button>
                  <p className="product-title">
                    {recommendation.title || "Uden titel"}
                  </p>
                  <p className="product-id">{recommendation.text}</p>
                  <p className="product-id">
                    <b>ID:</b> {recommendation.id}
                  </p>
                  <p className="product-id">
                    <b>Scope:</b>{" "}
                    {recommendation.scopeType === "GLOBAL" ? "Global" : "Kampagne"}
                  </p>
                  {recommendation.scopeType !== "GLOBAL" && recommendation.campaignId && (
                    <p className="product-id">
                      <b>Kampagne:</b> {this.getCampaignName(recommendation.campaignId)}
                    </p>
                  )}
                  <p className="product-id">
                    <b>Rapporter:</b> {recommendation.reportCount ?? 0}
                  </p>
                  <p className="product-id">
                    <b>Brugt:</b> {recommendation.timesUsed ?? 0}
                  </p>
                  <p className="product-id">
                    <b>Oprettet af:</b>{" "}
                    {recommendation.createdByName ||
                      recommendation.createdByUserId ||
                      "Ukendt"}
                  </p>
                  <p className="product-id">
                    <b>Oprettet:</b>{" "}
                    {recommendation.createdAt
                      ? new Date(recommendation.createdAt).toLocaleString("da-DK")
                      : ""}
                  </p>
                  <p className="product-id">
                    <b>Link:</b>{" "}
                    {recommendation.link ? (
                      <a
                        href={recommendation.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {recommendation.link}
                      </a>
                    ) : (
                      "Ingen"
                    )}
                  </p>
                </div>
              ))}
            </div>
            {this.state.nextToken && (
              <button
                type="button"
                className="button is-info is-light"
                disabled={this.state.loadingMore}
                onClick={() => this.fetchRecommendations(this.state.nextToken)}
              >
                {this.state.loadingMore ? "Henter..." : "Hent flere"}
              </button>
            )}
          </div>
        </div>
      </>
    );
  }
}
